import type { Dispatch, ReactElement, SetStateAction } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'lucide-react';
import { GradeFlowShell } from './GradeFlowShell';
import { GradePipelineSteps } from './GradePipelineSteps';
import { PageWrapper } from '../PageWrapper';
import type { GradeSyncPreflightReceipt } from './GradeSyncPreflightView';

export type GradeFinaleViewProps = {
  courseNameHeader?: string;
  isOnline: boolean;
  isDarkMode: boolean;
  setIsDarkMode: Dispatch<SetStateAction<boolean>>;
  syncStatus: 'idle' | 'ok' | 'error';
  onSyncClick: () => void;
  syncReceipts: GradeSyncPreflightReceipt[];
  gradeSyncPostToClassroom: boolean;
  gradeSyncEmailStudents: boolean;
  gradeSyncSaveToDrive: boolean;
  onBackToGrade: () => void;
};

export function GradeFinaleView({
  courseNameHeader,
  isOnline,
  isDarkMode,
  setIsDarkMode,
  syncStatus,
  onSyncClick,
  syncReceipts,
  gradeSyncPostToClassroom,
  gradeSyncEmailStudents,
  gradeSyncSaveToDrive,
  onBackToGrade,
}: GradeFinaleViewProps): ReactElement {
  const { t } = useTranslation();
  const summary = useMemo(() => {
    const emailSent = syncReceipts.filter((r) => r.emailOk === true).length;
    const emailFailed = syncReceipts.filter((r) => r.emailOk === false).length;
    const emailSkipped = syncReceipts.filter((r) => r.emailSkipped === true).length;
    const emailMissing = syncReceipts.filter((r) => r.emailMissing === true).length;
    const drivePagesSaved = syncReceipts.reduce(
      (sum, r) => sum + (r.driveSkipped ? 0 : (r.drivePagesSaved ?? 0)),
      0
    );
    const driveSkipped = syncReceipts.filter((r) => r.driveSkipped === true).length;
    const classroomSkipped = syncReceipts.filter((r) => r.classroomSkipped === true).length;
    const classroomPosted = syncReceipts.length - classroomSkipped;
    return {
      emailSent,
      emailFailed,
      emailSkipped,
      emailMissing,
      drivePagesSaved,
      driveSkipped,
      classroomPosted,
    };
  }, [syncReceipts]);

  return (
    <PageWrapper
      headerTitle={t('gradeSync.finale.headerTitle')}
      headerSubtitle={courseNameHeader || undefined}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={onSyncClick}
    >
      <GradeFlowShell
        step={t('gradeSync.finale.stepLabel')}
        title={t('gradeSync.finale.title')}
        subtitle={t('gradeSync.finale.subtitle')}
      >
        <GradePipelineSteps activeIndex={4} className="mb-4" />
        <div className="flex flex-col items-center justify-center py-4 text-center">
          <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg">
            <CheckCircle className="h-10 w-10" />
          </div>
          <h2 className="mb-2 text-3xl font-black text-emerald-500">
            {t('gradeSync.finale.publishedHeading')}
          </h2>
          <p className="mb-2 font-bold text-[16px] text-slate-500">
            {t('gradeSync.finale.publishingComplete')}
          </p>
          <p className="text-slate-500 font-bold text-[12px] mb-1">
            {t('gradeSync.preflight.statClassroom')}:{' '}
            {gradeSyncPostToClassroom
              ? t('gradeSync.finale.classroomPosted', { count: summary.classroomPosted })
              : t('gradeSync.preflight.skipped')}
          </p>
          <p className="text-slate-500 font-bold text-[12px] mb-1">
            {t('gradeSync.preflight.statEmail')}:{' '}
            {gradeSyncEmailStudents
              ? t('gradeSync.finale.emailSent', { count: summary.emailSent })
              : t('gradeSync.preflight.skipped')}
            {gradeSyncEmailStudents && summary.emailFailed > 0
              ? t('gradeSync.finale.emailFailedSuffix', { count: summary.emailFailed })
              : ''}
            {gradeSyncEmailStudents && summary.emailMissing > 0
              ? t('gradeSync.finale.emailMissingSuffix', { count: summary.emailMissing })
              : ''}
            {gradeSyncEmailStudents && summary.emailSkipped > 0
              ? t('gradeSync.finale.emailSkippedSuffix', { count: summary.emailSkipped })
              : ''}
          </p>
          <p className="text-slate-500 font-bold text-[12px] mb-8">
            {t('gradeSync.preflight.statDrive')}:{' '}
            {gradeSyncSaveToDrive
              ? t('gradeSync.finale.drivePagesSaved', { count: summary.drivePagesSaved })
              : t('gradeSync.preflight.skipped')}
            {gradeSyncSaveToDrive && summary.driveSkipped > 0
              ? t('gradeSync.finale.driveItemsSkippedSuffix', { count: summary.driveSkipped })
              : ''}
          </p>
          <button
            type="button"
            onClick={onBackToGrade}
            className="rounded-xl bg-indigo-600 px-8 py-4 font-black uppercase tracking-widest text-[16px] text-white shadow-sm transition-all hover:-translate-y-0.5"
          >
            {t('gradeSync.finale.backToGrade')}
          </button>
        </div>
      </GradeFlowShell>
    </PageWrapper>
  );
}
